import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { useUnit } from 'effector-react';
import { apartmentsApi } from '../api/apartments';
import { buildingsApi } from '../api/buildings';
import { $isSettlementTenant } from '../store/auth';
import { getObjectKindLabels } from '../utils/complexKind';

const surfaceSx = {
  borderRadius: 4,
  border: '1px solid var(--border-color)',
  background: 'var(--surface-bg)',
  boxShadow: 'none',
};

const inputSx = {
  '& .MuiInputLabel-root': {
    fontSize: 13,
    color: '#64748B',
  },
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    background: '#F8FAFD',
    borderRadius: 2,
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.75)',
    '& fieldset': {
      borderColor: 'rgba(38, 65, 94, 0.16)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(46, 115, 184, 0.48)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2E73B8',
      borderWidth: 1,
    },
  },
};

const autocompletePaperSx = {
  mt: 0.6,
  borderRadius: 2,
  border: '1px solid rgba(38, 65, 94, 0.16)',
  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    p: 0.5,
    maxHeight: 320,
  },
  '& .MuiAutocomplete-option': {
    minHeight: 34,
    borderRadius: 1.25,
    fontSize: 13,
    '&[aria-selected="true"]': {
      backgroundColor: 'rgba(46, 115, 184, 0.1)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(46, 115, 184, 0.08)',
    },
  },
};

const countChipSx = {
  height: 22,
  borderRadius: '999px',
  fontSize: 12,
  fontWeight: 700,
  background: '#F4F7FB',
  color: '#26415E',
  border: '1px solid rgba(38, 65, 94, 0.12)',
};

const tableHeadCellSx = {
  py: 0.9,
  px: 1.15,
  position: 'sticky',
  top: 0,
  zIndex: 3,
  whiteSpace: 'nowrap',
  fontSize: 12,
  fontWeight: 800,
  color: '#526579',
  background: 'linear-gradient(180deg, #FFFFFF 0%, #F6F9FD 100%)',
  borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.06)',
};

const tableCellSx = {
  py: 0.75,
  px: 1.15,
  fontSize: 13,
  borderColor: 'rgba(15, 23, 42, 0.06)',
  verticalAlign: 'middle',
};

const systemLabels = [
  { key: 'devices_hvs', label: 'ХВС' },
  { key: 'devices_gvs', label: 'ГВС' },
  { key: 'devices_heating', label: 'Отопление' },
  { key: 'devices_electricity', label: 'Электро' },
  { key: 'devices_urt', label: 'УРТ' },
];

function formatArea(value) {
  if (value == null || value === '') return '—';
  const numeric = Number(String(value).replace(',', '.'));
  if (!Number.isFinite(numeric)) return String(value);
  return `${numeric.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} м²`;
}

function formatReductionCoefficients(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return '—';
  }

  return values
    .map((value) =>
      Number(value).toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      })
    )
    .join(', ');
}

function buildDeviceChips(row) {
  return systemLabels
    .map((item) => ({
      label: item.label,
      value: Number(row[item.key] || 0),
    }))
    .filter((item) => item.value > 0);
}

function needsCheck(row) {
  return Array.isArray(row?.quality_warnings) && row.quality_warnings.length > 0;
}

const HEAT_CALC_COLUMN_HINT =
  'Как считать отопление в помещении. «По приборам» — по показаниям УРТ. «По площади» — без УРТ, доля от площади помещения.';

const PremisesSearchInput = React.memo(function PremisesSearchInput({
  onSearchChange,
  placeholder = 'Помещение, счёт, собственник, прибор',
}) {
  const [value, setValue] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    setApplying(value.trim().length > 0);
    const timerId = window.setTimeout(() => {
      onSearchChange(value);
      setApplying(false);
    }, 260);

    return () => window.clearTimeout(timerId);
  }, [onSearchChange, value]);

  return (
    <TextField
      size="small"
      label="Поиск"
      value={value}
      onChange={(event) => setValue(event.target.value)}
      placeholder={placeholder}
      sx={{ flex: 1, minWidth: { xs: '100%', md: 280 }, ...inputSx }}
      InputProps={{
        startAdornment: (
          <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
        ),
        endAdornment: applying ? (
          <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
        ) : null,
      }}
    />
  );
});

export default function PremisesPage() {
  const navigate = useNavigate();
  const isSettlementTenant = useUnit($isSettlementTenant);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );
  const [buildings, setBuildings] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(true);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [savingAreaModeId, setSavingAreaModeId] = useState(null);
  const [error, setError] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [needsCheckOnly, setNeedsCheckOnly] = useState(false);
  const collator = useMemo(
    () => new Intl.Collator('ru', { numeric: true, sensitivity: 'base' }),
    []
  );

  useEffect(() => {
    let active = true;

    const loadBuildings = async () => {
      setLoadingBuildings(true);
      try {
        const items = await buildingsApi.getBuildings(0, 500);
        if (!active) return;
        setBuildings(items || []);
        setSelectedBuilding((current) => current || items?.[0] || null);
      } catch (loadError) {
        console.error('load buildings failed', loadError);
        if (active) {
          setBuildings([]);
          setError('Не удалось загрузить список домов.');
        }
      } finally {
        if (active) {
          setLoadingBuildings(false);
        }
      }
    };

    loadBuildings();
    return () => {
      active = false;
    };
  }, []);

  const loadSummary = useCallback(async () => {
    if (!selectedBuilding?.id) {
      setSummary(null);
      return;
    }

    setLoadingSummary(true);
    setError('');

    try {
      const response = await apartmentsApi.getBuildingSummary(selectedBuilding.id);
      setSummary(response);
    } catch (loadError) {
      console.error('load premises summary failed', loadError);
      setSummary(null);
      setError(`Не удалось загрузить сводку по ${labels.premises.toLowerCase()}.`);
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedBuilding, labels.premises]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleAreaModeChange = async (row, checked) => {
    setSavingAreaModeId(row.apartment_id);
    setError('');
    try {
      await apartmentsApi.update(row.apartment_id, { heat_calculate_by_area: checked });
      await loadSummary();
    } catch (saveError) {
      console.error('save apartment heat area mode failed', saveError);
      setError(`Не удалось изменить режим расчёта ${labels.premise.toLowerCase()}.`);
    } finally {
      setSavingAreaModeId(null);
    }
  };

  const openApartment = useCallback((apartmentId) => {
    if (apartmentId) {
      navigate(`/apartments/${apartmentId}`);
    }
  }, [navigate]);

  const filteredItems = useMemo(() => {
    const items = summary?.items || [];
    const query = debouncedSearch.trim().toLowerCase();
    const sortedItems = [...items].sort((left, right) => {
      const corpCompare = collator.compare(left.corp_name || '', right.corp_name || '');
      if (corpCompare !== 0) return corpCompare;
      const numberCompare = collator.compare(left.display_number || '', right.display_number || '');
      if (numberCompare !== 0) return numberCompare;
      return collator.compare(left.account_number || '', right.account_number || '');
    });

    return sortedItems.filter((row) => {
      if (needsCheckOnly && !needsCheck(row)) {
        return false;
      }

      if (!query) return true;

      const haystack = [
        row.display_number,
        row.corp_name,
        row.owner_name,
        row.account_number,
        ...(row.quality_warnings || []),
        ...(row.device_systems || []),
        ...(row.device_serials || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [debouncedSearch, needsCheckOnly, summary, collator]);

  const needsCheckTotal = useMemo(
    () => (summary?.items || []).filter((row) => needsCheck(row)).length,
    [summary]
  );

  const filteredTotals = useMemo(() => {
    return filteredItems.reduce(
      (acc, row) => {
        acc.apartments += 1;
        acc.devices += Number(row.devices_total || 0);
        if (needsCheck(row)) {
          acc.qualityWarnings += 1;
        }

        const numericArea = Number(String(row.area ?? '').replace(',', '.'));
        if (Number.isFinite(numericArea)) {
          acc.area += numericArea;
        }

        return acc;
      },
      { apartments: 0, devices: 0, area: 0, qualityWarnings: 0 }
    );
  }, [filteredItems]);

  return (
    <div className="home-container" style={{ marginLeft: 24, marginRight: 24, paddingBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          ...surfaceSx,
          p: { xs: 1, md: 1.15 },
          mb: 1,
          borderRadius: 3,
          background: '#fff',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={0.8}
          justifyContent="flex-end"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0.75}
            sx={{ width: '100%' }}
          >
            <Autocomplete
              size="small"
              loading={loadingBuildings}
              options={buildings}
              value={selectedBuilding}
              onChange={(event, value) => setSelectedBuilding(value)}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              slotProps={{ paper: { sx: autocompletePaperSx } }}
              sx={{ flex: { xs: '1 1 auto', md: '0 0 360px' }, ...inputSx }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Дом"
                  placeholder="Выберите дом"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingBuildings ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <PremisesSearchInput
              onSearchChange={setDebouncedSearch}
              placeholder={`${labels.premise}, счёт, собственник, прибор`}
            />

            <FormControlLabel
              sx={{
                m: 0,
                px: 1,
                minHeight: 40,
                borderRadius: 2,
                border: '1px solid',
                borderColor: needsCheckOnly ? 'rgba(245, 158, 11, 0.45)' : 'rgba(38, 65, 94, 0.16)',
                background: needsCheckOnly ? '#FFF7ED' : '#F8FAFD',
                whiteSpace: 'nowrap',
                '& .MuiFormControlLabel-label': {
                  fontSize: 13,
                  fontWeight: 700,
                  color: needsCheckOnly ? '#B45309' : '#26415E',
                },
              }}
              control={
                <Switch
                  size="small"
                  checked={needsCheckOnly}
                  onChange={(event) => setNeedsCheckOnly(event.target.checked)}
                  color="warning"
                />
              }
              label={`На проверку${needsCheckTotal ? ` · ${needsCheckTotal}` : ''}`}
            />
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 1.25 }}>
        {[
          { key: 'premises', label: labels.premises, value: filteredTotals.apartments },
          {
            key: 'area',
            label: 'Площадь',
            value: `${filteredTotals.area.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} м²`,
          },
          { key: 'devices', label: 'Приборов', value: filteredTotals.devices },
          {
            key: 'check',
            label: 'Проверить',
            value: needsCheckOnly ? filteredTotals.qualityWarnings : needsCheckTotal,
            clickable: true,
            active: needsCheckOnly,
            onClick: () => setNeedsCheckOnly((current) => !current),
            hint: needsCheckOnly
              ? 'Показаны только помещения с замечаниями. Нажмите, чтобы сбросить фильтр.'
              : 'Показать только помещения, которые надо проверить.',
          },
        ].map((item) => (
          <Tooltip key={item.key} title={item.hint || ''} disableHoverListener={!item.hint}>
            <Paper
              variant="outlined"
              onClick={item.onClick}
              sx={{
                ...surfaceSx,
                p: 1.15,
                flex: 1,
                cursor: item.clickable ? 'pointer' : 'default',
                borderColor: item.active ? 'rgba(245, 158, 11, 0.45)' : undefined,
                background: item.active ? '#FFF7ED' : undefined,
                transition: 'background-color 140ms ease, border-color 140ms ease',
                '&:hover': item.clickable
                  ? {
                      borderColor: 'rgba(245, 158, 11, 0.55)',
                      background: item.active ? '#FFEDD5' : '#FFFBEB',
                    }
                  : undefined,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: item.active ? '#B45309' : 'text.secondary',
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  color: item.active ? '#B45309' : 'inherit',
                }}
              >
                {item.value}
              </Typography>
            </Paper>
          </Tooltip>
        ))}
      </Stack>

      <Paper variant="outlined" sx={{ ...surfaceSx, overflow: 'hidden' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={0.75}
          sx={{
            px: 1.25,
            py: 0.85,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFCFF 100%)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#526579', fontWeight: 700, display: 'block' }}>
              {selectedBuilding?.name || 'Дом не выбран'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.15 }}>
              Колонка «Расчёт тепла»: по приборам — по УРТ; по площади — без показаний УРТ.
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`Строк: ${filteredItems.length}`} sx={countChipSx} />
            {needsCheckOnly ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<WarningAmberRoundedIcon />}
                label="Только на проверку"
                onDelete={() => setNeedsCheckOnly(false)}
                sx={{
                  ...countChipSx,
                  color: '#B45309',
                  background: '#FFF7ED',
                  borderColor: 'rgba(245, 158, 11, 0.35)',
                  '& .MuiChip-icon': { color: '#D97706' },
                }}
              />
            ) : null}
            {debouncedSearch.trim() ? <Chip size="small" label="Поиск активен" sx={countChipSx} /> : null}
          </Stack>
        </Stack>
        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 330px)',
            minHeight: 280,
            overflow: 'auto',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': {
              width: 10,
              height: 10,
            },
            '&::-webkit-scrollbar-track': {
              background: '#F5F7FB',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#CBD5E1',
              borderRadius: 999,
              border: '2px solid #F5F7FB',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94A3B8',
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ minWidth: 1320 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: 110 }}>{labels.premise}</TableCell>
                {!isSettlementTenant && (
                  <>
                    <TableCell sx={{ ...tableHeadCellSx, width: 120 }}>Корпус</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, width: 70 }}>Этаж</TableCell>
                    <TableCell sx={{ ...tableHeadCellSx, width: 80 }}>Секция</TableCell>
                  </>
                )}
                <TableCell sx={{ ...tableHeadCellSx, width: 100 }}>Площадь</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 150 }}>Лицевой счёт</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 190 }}>Собственник</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 80 }}>Приборов</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 110 }}>Коэф.</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 175 }}>
                  <Tooltip title={HEAT_CALC_COLUMN_HINT}>
                    <Stack direction="row" spacing={0.4} alignItems="center" component="span">
                      <Box component="span">Расчёт тепла</Box>
                      <InfoOutlinedIcon sx={{ fontSize: 14, color: '#94A3B8' }} />
                    </Stack>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 115 }}>Проверка</TableCell>
                <TableCell sx={tableHeadCellSx}>Приборы</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingSummary ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ py: 6, textAlign: 'center' }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    {!selectedBuilding
                      ? 'Выберите дом.'
                      : needsCheckOnly
                        ? `Нет ${labels.premises.toLowerCase()} на проверку${
                            debouncedSearch.trim() ? ' по текущему поиску' : ''
                          }.`
                        : `По выбранному дому ${labels.premises.toLowerCase()} не найдены.`}
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((row) => {
                  const chips = buildDeviceChips(row);
                  const heatByArea = Boolean(row.heat_calculate_by_area);

                  return (
                    <TableRow
                      key={row.apartment_id}
                      hover
                      onClick={() => openApartment(row.apartment_id)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 140ms ease',
                        '&:nth-of-type(even)': {
                          backgroundColor: 'rgba(248, 250, 252, 0.55)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(52, 104, 255, 0.055)',
                        },
                      }}
                    >
                      <TableCell sx={tableCellSx}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#2368B5' }}>
                          {row.display_number}
                        </Typography>
                      </TableCell>
                      {!isSettlementTenant && (
                        <>
                          <TableCell sx={tableCellSx}>
                            {row.corp_name && row.corp_name !== 'Без корпуса' ? row.corp_name : '—'}
                          </TableCell>
                          <TableCell sx={tableCellSx}>{row.floor_number || '—'}</TableCell>
                          <TableCell sx={tableCellSx}>{row.section_number || '—'}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ ...tableCellSx, whiteSpace: 'nowrap', fontWeight: 700 }}>
                        {formatArea(row.area)}
                      </TableCell>
                      <TableCell sx={{ ...tableCellSx, whiteSpace: 'nowrap' }}>{row.account_number || '—'}</TableCell>
                      <TableCell sx={tableCellSx}>
                        <Box sx={{ maxWidth: 190, whiteSpace: 'normal', lineHeight: 1.3 }}>
                          {row.owner_name || '—'}
                        </Box>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Chip label={row.devices_total} size="small" sx={countChipSx} />
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Box sx={{ minWidth: 70, whiteSpace: 'normal', lineHeight: 1.3 }}>
                          {formatReductionCoefficients(row.reduction_coefficients)}
                        </Box>
                      </TableCell>
                      <TableCell sx={tableCellSx} onClick={(event) => event.stopPropagation()}>
                        <Tooltip
                          title={
                            heatByArea
                              ? 'Отопление считается по площади, показания УРТ не используются. Переключите, чтобы считать по приборам.'
                              : 'Отопление считается по показаниям УРТ. Переключите, чтобы считать по площади без приборов.'
                          }
                        >
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Switch
                              size="small"
                              checked={heatByArea}
                              disabled={savingAreaModeId === row.apartment_id}
                              onChange={(event) => handleAreaModeChange(row, event.target.checked)}
                              inputProps={{
                                'aria-label': heatByArea
                                  ? 'Расчёт отопления по площади'
                                  : 'Расчёт отопления по приборам',
                              }}
                            />
                            <Box sx={{ minWidth: 0, lineHeight: 1.2 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  fontWeight: 800,
                                  color: heatByArea ? '#B45309' : '#047857',
                                }}
                              >
                                {heatByArea ? 'По площади' : 'По приборам'}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', color: '#64748B', fontSize: 11 }}
                              >
                                {heatByArea ? 'без УРТ' : 'по УРТ'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        {Array.isArray(row.quality_warnings) && row.quality_warnings.length > 0 ? (
                          <Tooltip title={row.quality_warnings.join(' ')}>
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              icon={<WarningAmberRoundedIcon />}
                              label="Есть вопрос"
                              sx={{
                                ...countChipSx,
                                color: '#B45309',
                                background: '#FFF7ED',
                                borderColor: 'rgba(245, 158, 11, 0.35)',
                                '& .MuiChip-icon': { color: '#D97706' },
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Chip
                            size="small"
                            label="Норма"
                            sx={{
                              ...countChipSx,
                              color: '#047857',
                              background: '#ECFDF5',
                              borderColor: 'rgba(16, 185, 129, 0.24)',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={tableCellSx}>
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          {chips.length > 0 ? (
                            chips.map((chip) => (
                              <Chip
                                key={`${row.apartment_id}-${chip.label}`}
                                size="small"
                                label={`${chip.label}: ${chip.value}`}
                                sx={countChipSx}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Нет приборов
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
